<script lang="ts" setup>
  import type { Entry } from 'contentful'
  import { restaurantsBreadcrumbSteps } from '@/constants/restaurant'
  import type { Restaurant } from '@/types/api/restaurant'
  import type { TypePageSkeleton } from '@/types/contentful'
  import { SITE_ENVIRONMENTS } from '~/constants/siteEnvironment'

  const $config = useRuntimeConfig()
  const useRestaurantFinder = $config.public.siteEnvironment === SITE_ENVIRONMENTS.AUSTRALIA

  // Australian site restaurant finder mode

  /**
   * @description Load the Google Maps API
   */
  function useGoogleMapsApi() {
    if (!useRestaurantFinder) {
      return ref(null)
    }

    const hasGoogleMapsApiLoaded = ref(false)

    try {
      const autoCompleteService = new google.maps.places.AutocompleteService()
      const geocoder = new google.maps.Geocoder()

      if (autoCompleteService && geocoder) {
        // Google places has already been initialised
        hasGoogleMapsApiLoaded.value = true
        return hasGoogleMapsApiLoaded
      }
    } catch (error: unknown) {
      console.error('Google Maps API failed to load', error)
    }

    useHead({
      script: [
        {
          async: true,
          type: 'text/javascript',
          src: `https://maps.googleapis.com/maps/api/js?key=${$config.public.googleMapsApiKey}&libraries=places`,
          onload: () => {
            hasGoogleMapsApiLoaded.value = true
          },
          onerror: () => {
            console.error('Google Maps API failed to load')
          }
        }
      ]
    })

    return hasGoogleMapsApiLoaded
  }

  const hasGoogleMapsApiLoaded = useGoogleMapsApi()

  const nearbyRestaurants = ref<Restaurant[] | null>(null)

  const restaurantFinderResult = await useAsyncData(async () => {
    if (!useRestaurantFinder) {
      return null
    }

    const result =
      await useFetch<Entry<TypePageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>>(`/api/cms/page/restaurants`)

    return result.data.value
  })

  const data = restaurantFinderResult.data
  const error = restaurantFinderResult.error

  if (useRestaurantFinder) {
    if (!data.value || error.value) {
      throw createError({
        ...error.value,
        statusCode: 404,
        statusMessage: 'Page not found',
        fatal: true
      })
    }

    useGrilldServerSeoMeta(data.value?.fields.seo)
    useGrilldSeoHead({
      pageTitle: data.value?.fields.seo?.fields.metaTitle ?? undefined,
      canonicalUrl: data.value?.fields.seo?.fields.canonicalUrl ?? undefined,
      jsonLinkingData: data.value?.fields.seo?.fields.jsonLinkingData ?? undefined
    })
  }

  // regular slug page mode

  const slugResult = await useAsyncData(async () => {
    if (useRestaurantFinder) {
      return null
    }

    const result =
      await useFetch<Entry<TypePageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>>(`/api/cms/page/restaurants`)

    return result.data.value
  })

  const slugData = slugResult.data
  const slugError = slugResult.error

  if (!useRestaurantFinder) {
    if (!slugData.value || slugError.value) {
      throw createError({
        statusCode: 404,
        statusMessage: 'No Restaurants Data. Page not found',
        fatal: true
      })
    }

    if (slugData.value.fields.redirect) {
      navigateTo(slugData.value.fields.redirect, {
        replace: true,
        redirectCode: 301
      })
    }

    useGrilldServerSeoMeta(slugData.value?.fields.seo)
    useGrilldSeoHead({
      pageTitle: slugData.value?.fields.seo?.fields.metaTitle ?? undefined,
      canonicalUrl: slugData.value?.fields.seo?.fields.canonicalUrl ?? undefined,
      jsonLinkingData: slugData.value?.fields.seo?.fields.jsonLinkingData ?? undefined
    })
  }
</script>
<template>
  <!-- Australia restaurant page mode -->
  <div v-if="useRestaurantFinder && data" class="restaurants-page-container">
    <section class="section-controls module-paddings">
      <CBreadcrumbs :steps="restaurantsBreadcrumbSteps" />
      <h1 class="header-4">Find Your Local</h1>
      <p class="body">find your nearest Grill'd restaurant and grab a burger today</p>

      <div class="location-search-container">
        <client-only>
          <CLocationSearch v-if="hasGoogleMapsApiLoaded" @restaurants="nearbyRestaurants = $event" />
        </client-only>
      </div>
    </section>

    <section class="section-results module-paddings-h">
      <div v-if="nearbyRestaurants === null" class="placeholder placeholder-location module-paddings-v">
        <icon-grilld-hand-holding-globe aria-hidden="true" class="icon-globe" />
        <p class="body-large">Please enter your location to find your nearest Grill’d</p>
      </div>

      <div v-else-if="nearbyRestaurants.length === 0" class="placeholder placeholder-no-results module-paddings-v">
        <p class="body-large">
          There are no Grill’d restaurants near your location. Please update your location and try again
        </p>
      </div>

      <div v-else class="nearby-restaurants-results module-paddings-v">
        <p class="body-large-strong">Nearby Restaurants</p>

        <CRestaurantCard v-for="restaurant in nearbyRestaurants" :key="restaurant.id" :restaurant="restaurant" />
      </div>
    </section>

    <MPageModules :page-data="data" />
  </div>

  <!-- regular slug page mode -->
  <MPageModules v-if="!useRestaurantFinder && slugData" :page-data="slugData" />
</template>

<style lang="scss" scoped>
  .restaurants-page-container {
    flex: 1;
    background: var(--color-secondary-paper-light);
  }

  .section-controls {
    background-color: var(--color-base-white);

    .header-4 {
      margin-top: 8px;
      margin-bottom: 8px;
    }

    p {
      margin-bottom: 24px;
    }

    .location-search-container {
      min-height: 112px;

      @include screenSmAndUp {
        min-height: 52px;
      }
    }
  }

  .section-results {
    flex: 1;

    display: flex;
    flex-direction: column;
    align-items: center;

    padding-bottom: 112px;

    @include screenMdAndUp {
      padding-bottom: 144px;
    }

    .placeholder {
      margin-top: 64px;
      text-align: center;
      color: var(--color-stroke-medium);
    }

    .placeholder-location {
      max-width: 30ch;

      .icon-globe {
        margin: auto auto 16px;

        width: 100px;
        height: 120px;

        @include screenMdAndUp {
          width: 150px;
          height: 180px;
        }
      }
    }

    .placeholder-no-results {
      max-width: 58ch;
    }

    .nearby-restaurants-results {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 12px;

      p {
        margin-top: 12px;
        margin-bottom: 12px;
      }
    }
  }
</style>
